import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Button, TextField,
  MenuItem, Grid, Alert, CircularProgress, Avatar, Chip,
  Divider, Slider, Stack, IconButton, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, FormControl,
  InputLabel, Select,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PeopleIcon from '@mui/icons-material/People';
import { statusAPI, projectAPI } from '../../api';
import { useAuth } from '../../context/AuthContext';

const STATUS_OPTIONS = ['In Progress', 'Completed', 'Blocked', 'On Hold', 'Review'];
const STATUS_COLORS = {
  'In Progress': { bg: '#1565C018', color: '#1565C0' },
  'Completed':   { bg: '#2E7D3218', color: '#2E7D32' },
  'Blocked':     { bg: '#C6282818', color: '#C62828' },
  'On Hold':     { bg: '#E6510018', color: '#E65100' },
  'Review':      { bg: '#7B1FA218', color: '#7B1FA2' },
};

const emptyForm = {
  tasks_completed: '', blockers: '', hours_worked: 0,
  start_time: '', end_time: '',
  completion_percentage: 0, task_status: 'In Progress',
  date: new Date().toISOString().split('T')[0],
};

export default function DailyStatusPage() {
  const { user } = useAuth();
  const [isBlocked, setIsBlocked] = useState(false);

  // Fetch fresh user data to check block_task (token may have stale data)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch((process.env.REACT_APP_API_URL || 'http://localhost:5000/api') + '/auth/me', {
      headers: { Authorization: `Bearer ${token}` }, credentials: 'include',
    })
      .then(r => r.json())
      .then(data => { if (data.user?.block_task === 1) setIsBlocked(true); })
      .catch(() => {});
  }, []);
  const isTeamMember = user?.role === 'team_member';
  const canSeeAll = user?.role === 'admin';

  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [statuses, setStatuses] = useState([]);
  const [members, setMembers] = useState([]);
  const [filterUser, setFilterUser] = useState('all');
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    projectAPI.getAll().then(res => {
      const ps = res.data.projects || [];
      setProjects(ps);
      if (ps.length > 0) setSelectedProject(ps[0].id);
    });
  }, []);

  useEffect(() => {
    if (!selectedProject) return;
    loadStatuses();
    // Load project members for filter dropdown (non-team-member roles)
    if (canSeeAll) {
      projectAPI.getMembers(selectedProject)
        .then(res => setMembers(res.data))
        .catch(() => {});
    }
  }, [selectedProject]);

  const loadStatuses = () => {
    setLoading(true);
    statusAPI.getAll(selectedProject)
      .then(res => setStatuses(res.data))
      .catch(() => setError('Failed to load statuses'))
      .finally(() => setLoading(false));
  };

  const openAdd = () => { setEditItem(null); setForm(emptyForm); setShowForm(true); };

  const openEdit = (status) => {
    setEditItem(status);
    setForm({
      date: status.date,
      tasks_completed: status.tasks_completed || '',
      blockers: status.blockers || '',
      hours_worked: parseFloat(status.hours_worked) || 8,
      completion_percentage: status.completion_percentage || 0,
      task_status: status.tasks_planned || 'In Progress',
      start_time: status.start_time || '',
      end_time: status.end_time || '',
    });
    setShowForm(true);
  };

  const handleSubmit = async () => {
    setError(''); setSuccess('');
    try {
      // Auto-calculate hours from time range
      let hoursWorked = 0;
      if (form.start_time && form.end_time) {
        const [sh, sm] = form.start_time.split(':').map(Number);
        const [eh, em] = form.end_time.split(':').map(Number);
        let diff = (eh * 60 + em) - (sh * 60 + sm);
        // Handle next-day end time (e.g. overnight shift)
        if (diff < 0) diff += 24 * 60;
        if (diff > 0) hoursWorked = Math.round((diff / 60) * 10) / 10;
      }
      const payload = {
        date: form.date,
        tasks_completed: form.tasks_completed,
        tasks_planned: form.task_status,
        blockers: form.blockers,
        hours_worked: hoursWorked,
        start_time: form.start_time || null,
        end_time: form.end_time || null,
        completion_percentage: form.completion_percentage,
      };
      if (editItem) {
        await statusAPI.update(selectedProject, editItem.id, payload);
        setSuccess('Status updated successfully!');
      } else {
        await statusAPI.add(selectedProject, payload);
        setSuccess('Status submitted successfully!');
      }
      setShowForm(false); setEditItem(null); setForm(emptyForm);
      loadStatuses();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save status');
    }
  };

  const handleDelete = async () => {
    try {
      await statusAPI.delete(selectedProject, deleteItem.id);
      setDeleteItem(null); setSuccess('Status entry deleted.');
      loadStatuses();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete.');
      setDeleteItem(null);
    }
  };

  const today = new Date().toISOString().split('T')[0];

  // Filter by selected user
  const filteredStatuses = filterUser === 'all'
    ? statuses
    : statuses.filter(s => s.user_id === parseInt(filterUser));

  // Group by date for non-team-member views
  const grouped = {};
  filteredStatuses.forEach(s => {
    if (!grouped[s.date]) grouped[s.date] = [];
    grouped[s.date].push(s);
  });
  const sortedDates = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, fontSize: { xs: '1.6rem', sm: '2rem' } }}>Daily Status</Typography>
            <Typography color="text.secondary" variant="body2" sx={{ mt: 0.3 }}>
              {isTeamMember ? 'Track your daily progress' : "View all team members' daily progress"}
            </Typography>
          </Box>
          <Tooltip title={isBlocked ? "Your account is blocked. Contact admin to unblock." : ""} arrow>
            <span>
              <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd} disabled={isBlocked}
                sx={{ whiteSpace: 'nowrap', flexShrink: 0, ...(isBlocked ? { bgcolor: '#9E9E9E !important', color: '#fff !important', boxShadow: 'none' } : {}) }}>
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Add </Box>Status
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={isTeamMember ? 12 : 8}>
              <TextField select fullWidth label="Select Project" value={selectedProject} size="small"
                onChange={(e) => { setSelectedProject(e.target.value); setFilterUser('all'); }}>
                {projects.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
              </TextField>
            </Grid>
            {canSeeAll && (
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Filter by Member</InputLabel>
                  <Select value={filterUser} label="Filter by Member"
                    onChange={(e) => setFilterUser(e.target.value)}
                    startAdornment={<PeopleIcon sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />}>
                    <MenuItem value="all">All Members</MenuItem>
                    {members.map(m => (
                      <MenuItem key={m.user_id} value={m.user_id}>
                        {m.member?.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {isBlocked && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2, fontWeight: 600 }}>
          🚫 Your account is <strong>blocked</strong> — you have not submitted a daily status for 3 consecutive working days. Please contact your Admin to unblock your account.
        </Alert>
      )}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}><CircularProgress /></Box>
      ) : !canSeeAll ? (
        // ── Team Member: flat list ──
        <Stack spacing={2}>
          {statuses.map((s) => <StatusCard key={s.id} s={s} today={today} onEdit={openEdit} onDelete={setDeleteItem} />)}
          {statuses.length === 0 && <EmptyState onAdd={openAdd} isBlocked={isBlocked} />}
        </Stack>
      ) : (
        // ── Other roles: grouped by date ──
        <Stack spacing={3}>
          {sortedDates.map(date => (
            <Box key={date}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.08em' }}>
                  {date === today ? '📅 Today' : `📅 ${date}`}
                </Typography>
                <Chip label={grouped[date].length + ' update' + (grouped[date].length > 1 ? 's' : '')}
                  size="small" variant="outlined" sx={{ fontSize: '0.65rem', fontWeight: 700 }} />
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
              </Box>
              <Stack spacing={1.5}>
                {grouped[date].map(s => <StatusCard key={s.id} s={s} today={today} onEdit={openEdit} onDelete={setDeleteItem} />)}
              </Stack>
            </Box>
          ))}
          {filteredStatuses.length === 0 && <EmptyState onAdd={openAdd} isBlocked={isBlocked} />}
        </Stack>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={showForm} onClose={() => { setShowForm(false); setEditItem(null); }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{editItem ? 'Edit Status Update' : "Today's Status Update"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ pt: 1 }}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField fullWidth label="Date" type="date" value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField fullWidth select label="Task Status" value={form.task_status}
                onChange={(e) => setForm({ ...form, task_status: e.target.value })}>
                {STATUS_OPTIONS.map(s => (
                  <MenuItem key={s} value={s}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: STATUS_COLORS[s]?.color }} />
                      {s}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField fullWidth label="Start Time" type="time" size="small"
                value={form.start_time || ''} onChange={e => setForm({ ...form, start_time: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField fullWidth label="End Time" type="time" size="small"
                value={form.end_time || ''} onChange={e => setForm({ ...form, end_time: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth required multiline rows={3} label="Task"
                placeholder="Describe what you worked on today..."
                value={form.tasks_completed} onChange={(e) => setForm({ ...form, tasks_completed: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="Blockers / Issues (if any)"
                placeholder="Any blockers or impediments..."
                value={form.blockers} onChange={(e) => setForm({ ...form, blockers: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>Overall Completion: <strong>{form.completion_percentage}%</strong></Typography>
              <Slider value={form.completion_percentage} min={0} max={100} step={5}
                onChange={(_, v) => setForm({ ...form, completion_percentage: v })}
                valueLabelDisplay="auto"
                marks={[{ value: 0, label: '0%' }, { value: 50, label: '50%' }, { value: 100, label: '100%' }]}
                sx={{ color: form.completion_percentage >= 80 ? 'success.main' : form.completion_percentage >= 40 ? 'warning.main' : 'primary.main' }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button variant="outlined" onClick={() => { setShowForm(false); setEditItem(null); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={!form.tasks_completed}>
            {editItem ? 'Save Changes' : 'Submit Status'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteItem} onClose={() => setDeleteItem(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Status Entry?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            Are you sure you want to delete the status entry for <strong>{deleteItem?.date}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button variant="outlined" onClick={() => setDeleteItem(null)}>Cancel</Button>
          <Button variant="contained" color="error" startIcon={<DeleteIcon />} onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function StatusCard({ s, today, onEdit, onDelete }) {
  const isToday = s.date === today;
  const taskStatus = s.tasks_planned || 'In Progress';
  const statusStyle = {
    'In Progress': { bg: '#1565C018', color: '#1565C0' },
    'Completed':   { bg: '#2E7D3218', color: '#2E7D32' },
    'Blocked':     { bg: '#C6282818', color: '#C62828' },
    'On Hold':     { bg: '#E6510018', color: '#E65100' },
    'Review':      { bg: '#7B1FA218', color: '#7B1FA2' },
  }[taskStatus] || { bg: '#1565C018', color: '#1565C0' };

  return (
    <Card sx={{ border: isToday ? '2px solid' : '1px solid', borderColor: isToday ? 'primary.light' : 'divider', borderRadius: 2 }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
        {/* Row 1: Avatar + Name + Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
            <Avatar sx={{ width: 34, height: 34, bgcolor: 'primary.main', fontSize: '0.8rem', fontWeight: 700, flexShrink: 0 }}>
              {s.author?.name?.charAt(0)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.author?.name}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                <AccessTimeIcon sx={{ fontSize: 11, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                  {s.date} · {s.start_time && s.end_time ? `${s.start_time} - ${s.end_time}` : `${s.hours_worked}h`}
                </Typography>
                {isToday && <Chip label="Today" size="small" color="primary" sx={{ height: 15, fontSize: '0.58rem', fontWeight: 700 }} />}
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={() => onEdit(s)} sx={{ width: 30, height: 30 }}><EditIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
            <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => onDelete(s)} sx={{ width: 30, height: 30 }}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
          </Box>
        </Box>
        {/* Row 2: Status chips */}
        <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', mb: 1.5 }}>
          <Chip label={taskStatus} size="small" sx={{ bgcolor: statusStyle.bg, color: statusStyle.color, fontWeight: 700, fontSize: '0.68rem' }} />
          <Chip icon={<CheckCircleOutlineIcon sx={{ fontSize: '13px !important' }} />}
            label={s.completion_percentage + '%'} size="small"
            color={s.completion_percentage >= 80 ? 'success' : s.completion_percentage >= 40 ? 'warning' : 'default'}
            variant="outlined" sx={{ fontWeight: 700, fontSize: '0.68rem' }} />
        </Box>
        <Divider sx={{ mb: 1.5 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'success.main', textTransform: 'uppercase', fontSize: '0.62rem', letterSpacing: '0.05em' }}>✅ Task</Typography>
            <Typography variant="body2" sx={{ mt: 0.3, whiteSpace: 'pre-wrap', fontSize: { xs: '0.82rem', sm: '0.875rem' } }}>{s.tasks_completed}</Typography>
          </Box>
          {s.blockers && s.blockers !== 'None' && (
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'warning.main', textTransform: 'uppercase', fontSize: '0.62rem', letterSpacing: '0.05em' }}>⚠️ Blockers</Typography>
              <Typography variant="body2" sx={{ mt: 0.3, whiteSpace: 'pre-wrap', color: 'error.main', fontSize: { xs: '0.82rem', sm: '0.875rem' } }}>{s.blockers}</Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

function EmptyState({ onAdd, isBlocked }) {
  return (
    <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'background.paper', borderRadius: 3, border: '1px dashed', borderColor: 'divider' }}>
      <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>No status updates yet</Typography>
      <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>Add your first daily status to get started</Typography>
      <Tooltip title={isBlocked ? "Your account is blocked. Contact admin to unblock." : ""} arrow>
        <span>
          <Button variant="contained" startIcon={<AddIcon />} onClick={onAdd} disabled={isBlocked}
            sx={isBlocked ? { bgcolor: '#9E9E9E !important', color: '#fff !important', boxShadow: 'none' } : {}}>
            Add Status
          </Button>
        </span>
      </Tooltip>
    </Box>
  );
}